import React, { useState, useEffect } from 'react';
import api from '../../api';

function ProgressBar({ value, max, color = '#0d9488' }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 4, height: 6, overflow: 'hidden', marginTop: 4 }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s' }} />
    </div>
  );
}

export default function ReportsView() {
  const [sales, setSales] = useState([]);
  const [belowPar, setBelowPar] = useState([]);
  const [scanHistory, setScanHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/sales/summary').catch(() => ({ data: [] })),
      api.get('/inventory?below_par=true').catch(() => ({ data: [] })),
      api.get('/inventory/scan-history').catch(() => ({ data: [] })),
    ]).then(([s, b, sc]) => {
      setSales(s.data);
      setBelowPar(b.data);
      setScanHistory(sc.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Loading reports...</div>;

  const topMovers = [...sales].sort((a, b) => b.qty_7d - a.qty_7d).slice(0, 10);
  const deadStock = sales.filter(s => parseFloat(s.qty_30d) === 0 && parseFloat(s.qty_7d) === 0);
  const maxQty = topMovers[0]?.qty_7d || 1;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="stat-card">
          <div className="stat-label">Items Below Par</div>
          <div className="stat-value" style={{ color: belowPar.length > 0 ? '#ef4444' : '#10b981' }}>{belowPar.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Scans</div>
          <div className="stat-value">{scanHistory.length}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Top Movers */}
        <div className="card">
          <div className="card-header">🔥 Top Movers (7 days)</div>
          <div className="card-body">
            {topMovers.length === 0 && <p className="text-gray text-sm">No sales recorded yet. Use Sales Entry to log daily counts.</p>}
            {topMovers.map(item => (
              <div key={item.id} style={{ marginBottom: 10 }}>
                <div className="flex justify-between" style={{ fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>{item.name}</span>
                  <span style={{ color: '#0d9488' }}>{parseFloat(item.qty_7d).toFixed(1)} {item.unit}</span>
                </div>
                <ProgressBar value={item.qty_7d} max={maxQty} color="#0d9488" />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>30d: {parseFloat(item.qty_30d).toFixed(1)} · avg/day: {parseFloat(item.avg_daily_7d).toFixed(1)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Below Par */}
        <div className="card">
          <div className="card-header">⚠️ Below Par Right Now</div>
          <div className="card-body">
            {belowPar.length === 0 && <p className="text-gray text-sm">All items are at or above par. 🎉</p>}
            {belowPar.map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, fontSize: 13 }}>
                <span style={{ fontWeight: 600 }}>{item.name}</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#ef4444', fontWeight: 700 }}>{item.quantity_on_hand} / {item.par_level} {item.unit}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>need {Math.max(0, item.par_level - item.quantity_on_hand)} more</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dead Stock */}
        <div className="card">
          <div className="card-header">💤 Dead Stock (0 sales in 30d)</div>
          <div className="card-body">
            {deadStock.length === 0 && <p className="text-gray text-sm">Everything is moving. Good job!</p>}
            {deadStock.length > 0 && (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Item</th><th>On Hand</th></tr></thead>
                  <tbody>
                    {deadStock.slice(0, 15).map(item => (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td className="text-gray">{item.qty_7d} {item.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Scan History */}
        <div className="card">
          <div className="card-header">📷 Recent Inventory Scans</div>
          <div className="card-body">
            {scanHistory.length === 0 && <p className="text-gray text-sm">No scans yet. Go to Inventory → Scan Sheet to start.</p>}
            {scanHistory.map(scan => (
              <div key={scan.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
                <div>
                  <span style={{ fontWeight: 600 }}>{scan.location || 'Full Kitchen'}</span>
                  <span style={{ marginLeft: 8, color: '#64748b' }}>{scan.item_count} items</span>
                </div>
                <span className="text-gray text-sm">{new Date(scan.scanned_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
